import React, { useRef } from "react";
import { Drawer } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getMemberSettings } from "utils/accessLocalStorageData";
import { getLocalStorageItem } from "utils/functions";

// Icons
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";

// Outlined tombstone with Star of David — stroke-based to match MUI outlined icon style
const YahrzeitIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {/* Tombstone arch */}
    <path d="M7 22V13a5 5 0 0 1 10 0V22" />
    {/* Ground base */}
    <line x1="4" y1="22" x2="20" y2="22" />
    {/* Star of David: upward triangle */}
    <polygon points="12,14 13.7,17 10.3,17" />
    {/* Star of David: downward triangle */}
    <polygon points="12,18 13.7,15 10.3,15" />
  </svg>
);

export const SIDEBAR_WIDTH = 240;

const SidebarContent = ({ onItemClick }) => {
  const history = useHistory();
  const location = useLocation();
  const memberSettings = getMemberSettings();
  const logo = getLocalStorageItem("getOrginasitionBySubDomain", true);
  const memberPortalDirectory = JSON.parse(
    localStorage.getItem("memberPortalDirectory")
  );

  const navItems = [
    { id: 0, title: "Home",           icon: <HomeOutlinedIcon fontSize="small" />,              url: "/home",                hide: false },
    { id: 1, title: "Family Members", icon: <PeopleAltOutlinedIcon fontSize="small" />,          url: "/family-member-table", hide: !memberSettings?.enableMembers },
    { id: 2, title: "Yahrzeit",       icon: <YahrzeitIcon />,                                    url: "/yahrzeit",           hide: !memberSettings?.enableYahrzeit },
    { id: 3, title: "Education",      icon: <SchoolOutlinedIcon fontSize="small" />,              url: "/classes",            hide: !memberSettings?.enableEducation },
    { id: 4, title: "Billing",        icon: <AttachMoneyOutlinedIcon fontSize="small" />,         url: "/finance",            hide: !memberSettings?.enableBilling },
    { id: 5, title: "Events",         icon: <EventOutlinedIcon fontSize="small" />,              url: "/events-view",         hide: !memberSettings?.enableEvents },
    { id: 6, title: "Donations",      icon: <VolunteerActivismOutlinedIcon fontSize="small" />,  url: "/donation-view",       hide: !memberSettings?.enableDonations },
    { id: 7, title: "Directory",      icon: <MenuBookOutlinedIcon fontSize="small" />,           url: "/directory",           hide: !memberPortalDirectory },
    { id: 8, title: "Settings",       icon: <SettingsOutlinedIcon fontSize="small" />,           url: "/settings",            hide: !memberSettings?.enableSettings },
  ];

  const navListRef = useRef(null);

  const visibleItems = navItems.filter((item) => !item.hide);

  const isActive = (url) =>
    location.pathname === url || location.pathname.startsWith(url + "/");

  const handleNav = (url) => {
    history.push(url);
    if (onItemClick) onItemClick();
  };

  const handleButtonKeyDown = (e, index) => {
    const count = visibleItems.length;
    let nextIndex = -1;
    if (e.key === "ArrowDown") nextIndex = (index + 1) % count;
    else if (e.key === "ArrowUp") nextIndex = (index - 1 + count) % count;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = count - 1;
    if (nextIndex === -1) return;
    e.preventDefault();
    const buttons = Array.from(navListRef.current ? navListRef.current.querySelectorAll("button") : []);
    if (buttons[nextIndex]) buttons[nextIndex].focus();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("hasPayment");
    localStorage.removeItem("userAuthData");
    window.location.reload();
  };

  return (
    <div className="psb-inner">
      {/* Nav items */}
      <ul className="psb-nav-area" aria-label="Main menu" ref={navListRef}>
        {visibleItems.map((item, index) => {
            const active = isActive(item.url);
            return (
              <li key={item.id}>
                <button
                  className={`psb-item${active ? " psb-item--active" : ""}`}
                  onClick={() => handleNav(item.url)}
                  onKeyDown={(e) => handleButtonKeyDown(e, index)}
                  aria-current={active ? "page" : undefined}
                >
                  <span className="psb-item__icon" aria-hidden="true">{item.icon}</span>
                  <span className="psb-item__label">{item.title}</span>
                  {active && <span className="psb-item__indicator" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
      </ul>

      <div className="psb-divider" />

      {/* Footer: app links + logout */}
      <div className="psb-footer">
        {logo?.memberPortalApp && (
          <div className="psb-app-links">
            <span className="psb-app-links__label">Get the app</span>
            <div className="psb-app-links__badges">
              <a href="https://apps.apple.com/il/app/easyshul-members/id6758013315" target="_blank" rel="noopener noreferrer">
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" height={28} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.members.easyshul" target="_blank" rel="noopener noreferrer">
                <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" height={36} />
              </a>
            </div>
          </div>
        )}
        <button className="psb-item psb-item--logout" onClick={handleLogout} aria-label="Logout">
          <span className="psb-item__icon" aria-hidden="true"><LogoutOutlinedIcon fontSize="small" /></span>
          <span className="psb-item__label">Logout</span>
        </button>
      </div>
    </div>
  );
};

const PortalSidebar = ({ mobileOpen, onClose }) => {
  return (
    <nav className="portal-sidebar-nav" aria-label="Site navigation">
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        className="portal-sidebar-mobile"
        PaperProps={{
          style: { width: SIDEBAR_WIDTH, border: "none", boxSizing: "border-box" },
        }}
      >
        <SidebarContent onItemClick={onClose} />
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        className="portal-sidebar-desktop"
        PaperProps={{
          style: { width: SIDEBAR_WIDTH, border: "none", boxSizing: "border-box", top: 0, height: "100%" },
        }}
        open
      >
        <SidebarContent />
      </Drawer>
    </nav>
  );
};

export default PortalSidebar;
